import { useApolloClient } from '@apollo/client';
import { Headphones } from 'lucide-react';
import React, { useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { AudioPlayer } from '@eluve/audio-player';
import { TooltipLabel } from '@eluve/blocks';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Divider,
  HStack,
  Icon,
  NewButton,
  Paragraph,
  VStack,
  useToast,
} from '@eluve/components';
import {
  AppointmentFileTypesEnum,
  UserUploadStatusEnum,
  UserUploadStatusLookup,
} from '@eluve/graphql-types';

import { appointmentFilesQuery } from './operations';

export interface AppointmentFileActionsColumnProps {
  tenantId: string;
  appointmentId: string;
  fileId: string;
  filePath: string;
  fileType: AppointmentFileTypesEnum;
  fileStatus?: UserUploadStatusEnum;
}

const NonAudioFileTypes: AppointmentFileTypesEnum[] = [
  'RAW_TRANSCRIPT_METADATA',
  'CHART_PDF',
  'SUPERBILL_PDF',
];

export const AppointmentFileActionsColumn: React.FC<
  AppointmentFileActionsColumnProps
> = ({ appointmentId, fileId, filePath, tenantId, fileType, fileStatus }) => {
  const api = useApiClient();
  const apolloClient = useApolloClient();
  const [audioUrl, setAudioUrl] = useState<string>('');
  const [markCancelDialogOpen, setMarkCancelDialogOpen] = useState(false);
  const [markCancelledProcessing, setMarkCancelledProcessing] = useState(false);
  const toast = useToast();
  const getFile = async (type: 'download' | 'listen') => {
    if (!filePath) return;
    const result = await api.adminAppointment.getAppointmentFileSignedUrl({
      params: { tenantId, appointmentId, fileId },
    });

    if (result.status === 200 && result.body.presignedUrl) {
      if (type === 'listen') {
        setAudioUrl(result.body.presignedUrl);
      } else {
        const fetched = await fetch(result.body.presignedUrl);
        const blob = await fetched.blob();
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = filePath.split('/').pop() ?? '';
        link.click();

        window.URL.revokeObjectURL(url);
      }
    } else {
      toast.error('Failed to generate file URL');
    }
  };

  const shouldShowMakAsCompleteOption =
    (fileType === 'USER_BACKUP_AUDIO' || fileType === 'CHUNKED_AUDIO') &&
    (fileStatus === 'PENDING_UPLOAD' || fileStatus === 'PRE_PROVISIONED');

  const onMarkCanceled = async () => {
    setMarkCancelledProcessing(true);

    const result =
      await api.adminAppointment.overrideAudioFileAndTranscriptAsCancelled({
        params: { tenantId, appointmentId, fileId },
      });

    if (result.status !== 200) {
      toast.error('Failed to mark file and/or transcript as complete');
    } else {
      apolloClient.refetchQueries({
        include: [appointmentFilesQuery],
      });
    }

    setMarkCancelledProcessing(false);
    setMarkCancelDialogOpen(false);
  };

  const onCancelMarkCanceled = () => {
    setMarkCancelDialogOpen(false);
  };

  if (!filePath) return null;

  return (
    <VStack>
      <HStack>
        {!NonAudioFileTypes.includes(fileType) && (
          <TooltipLabel label="Listen to audio">
            <Button
              variant="outline"
              size="icon"
              onClick={() => getFile('listen')}
              disabled={
                fileStatus !== UserUploadStatusLookup.AVAILABLE ||
                !filePath ||
                Boolean(audioUrl)
              }
            >
              <Headphones />
            </Button>
          </TooltipLabel>
        )}

        <TooltipLabel label="Download">
          <Button
            variant="outline"
            size="icon"
            onClick={() => getFile('download')}
            disabled={
              fileStatus !== UserUploadStatusLookup.AVAILABLE || !filePath
            }
          >
            <Icon name="Download" />
          </Button>
        </TooltipLabel>

        {shouldShowMakAsCompleteOption && (
          <Dialog
            open={markCancelDialogOpen}
            onOpenChange={setMarkCancelDialogOpen}
          >
            <DialogTrigger asChild>
              <div>
                <TooltipLabel label="Mark as canceled">
                  <NewButton icon={{ name: 'CircleX' }} />
                </TooltipLabel>
              </div>
            </DialogTrigger>
            <DialogContent>
              <div className="flex gap-3">
                <Icon name="CircleX" className="size-8" />
                <DialogHeader>
                  <DialogTitle className="font-normal tracking-normal text-text-secondary">
                    Mark as Canceled
                  </DialogTitle>
                  <DialogDescription>
                    Are you sure you want to mark this file as canceled?
                  </DialogDescription>
                </DialogHeader>
              </div>

              <Divider />

              <Paragraph size="md">
                This action will mark the audio file and associated transcript
                as canceled even though it currently appears to be missing.
              </Paragraph>
              <Paragraph size="md">
                A tag will automatically be added to this appointment and file
                indicating that human intervention was required.
              </Paragraph>

              <DialogFooter>
                <NewButton
                  text="Cancel"
                  onClick={onCancelMarkCanceled}
                  type="outline"
                  size="s"
                />

                <NewButton
                  text={
                    markCancelledProcessing
                      ? 'Cancelling, please wait...'
                      : 'Mark as Canceled'
                  }
                  onClick={onMarkCanceled}
                  disabled={markCancelledProcessing}
                  size="s"
                />
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </HStack>
      <HStack>
        {Boolean(!NonAudioFileTypes.includes(fileType) && audioUrl) && (
          <AudioPlayer audioUrl={audioUrl} />
        )}
      </HStack>
    </VStack>
  );
};
